import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiLock, FiMail, FiAlertCircle, FiCheck, FiArrowLeft, FiSend, FiKey } from 'react-icons/fi';
import { login, requestLoginOTP, verifyLoginOTP, requestPasswordReset, verifyResetOTP, resetPassword } from '../services/api';

function AdminLogin({ onLogin }) {
  // View states: 'login', 'otp-login', 'forgot-password', 'reset-password'
  const [view, setView] = useState('login');
  
  // Form fields
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState(['', '', '', '']);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  // UI states
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [resendTimer, setResendTimer] = useState(0);
  
  // OTP input refs
  const otpRefs = [useRef(), useRef(), useRef(), useRef()];
  
  // Resend timer effect
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  // Password login handler
  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const response = await login(email, password);
      const { access_token, role } = response.data;
      localStorage.setItem('admin_token', access_token);
      localStorage.setItem('admin_role', role);
      onLogin();
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid email or password');
    } finally {
      setIsLoading(false);
    }
  };

  // Request OTP for login
  const handleRequestLoginOTP = async () => {
    if (!email) {
      setError('Please enter your email address');
      return;
    }
    setError('');
    setIsLoading(true);

    try {
      await requestLoginOTP(email);
      setSuccess('OTP sent to your email!');
      setView('otp-login');
      setResendTimer(45);
      setOtp(['', '', '', '']);
      setTimeout(() => otpRefs[0].current?.focus(), 100);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send OTP. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Verify OTP and login
  const handleVerifyLoginOTP = async () => {
    const otpCode = otp.join('');
    if (otpCode.length !== 4) {
      setError('Please enter the complete 4-digit OTP');
      return;
    }
    setError('');
    setIsLoading(true);

    try {
      const response = await verifyLoginOTP(email, otpCode);
      const { access_token, role } = response.data;
      localStorage.setItem('admin_token', access_token);
      localStorage.setItem('admin_role', role);
      onLogin();
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid OTP. Please try again.');
      setOtp(['', '', '', '']);
      otpRefs[0].current?.focus();
    } finally {
      setIsLoading(false);
    }
  };

  // Request password reset OTP
  const handleRequestPasswordReset = async () => {
    if (!email) {
      setError('Please enter your email address');
      return;
    }
    setError('');
    setIsLoading(true);

    try {
      await requestPasswordReset(email);
      setSuccess('Password reset OTP sent to your email!');
      setView('forgot-password');
      setResendTimer(45);
      setOtp(['', '', '', '']);
      setTimeout(() => otpRefs[0].current?.focus(), 100);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send OTP. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Verify reset OTP
  const handleVerifyResetOTP = async () => {
    const otpCode = otp.join('');
    if (otpCode.length !== 4) {
      setError('Please enter the complete 4-digit OTP');
      return;
    }
    setError('');
    setIsLoading(true);

    try {
      await verifyResetOTP(email, otpCode);
      setSuccess('OTP verified! Set your new password.');
      setView('reset-password');
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid OTP. Please try again.');
      setOtp(['', '', '', '']);
      otpRefs[0].current?.focus();
    } finally {
      setIsLoading(false);
    }
  };

  // Reset password
  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    setError('');
    setIsLoading(true);

    try {
      const otpCode = otp.join('');
      await resetPassword(email, otpCode, newPassword);
      setSuccess('Password reset successfully! Please login with your new password.');
      setView('login');
      setPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setOtp(['', '', '', '']);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // OTP input handler
  const handleOtpChange = (index, value) => {
    if (value.length > 1) {
      value = value.slice(-1);
    }
    if (!/^\d*$/.test(value)) return;
    
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    
    // Auto-focus next input
    if (value && index < 3) {
      otpRefs[index + 1].current?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs[index - 1].current?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 4);
    if (/^\d+$/.test(pastedData)) {
      const newOtp = pastedData.split('').concat(['', '', '', '']).slice(0, 4);
      setOtp(newOtp);
      const focusIndex = Math.min(pastedData.length, 3);
      otpRefs[focusIndex].current?.focus();
    }
  };

  // Reset to login view
  const backToLogin = () => {
    setView('login');
    setError('');
    setSuccess('');
    setOtp(['', '', '', '']);
    setNewPassword('');
    setConfirmPassword('');
  };

  // Resend OTP
  const handleResendOTP = async () => {
    if (resendTimer > 0) return;
    
    setError('');
    setIsLoading(true);
    
    try {
      if (view === 'otp-login') {
        await requestLoginOTP(email);
      } else {
        await requestPasswordReset(email);
      }
      setSuccess('New OTP sent!');
      setResendTimer(45);
      setOtp(['', '', '', '']);
      otpRefs[0].current?.focus();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to resend OTP.');
    } finally {
      setIsLoading(false);
    }
  };

  // OTP Input Component
  const OTPInput = () => (
    <div className="flex justify-center gap-3 my-6">
      {otp.map((digit, index) => (
        <input
          key={index}
          ref={otpRefs[index]}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={digit}
          onChange={(e) => handleOtpChange(index, e.target.value)}
          onKeyDown={(e) => handleOtpKeyDown(index, e)}
          onPaste={handleOtpPaste}
          className="w-14 h-14 text-center text-2xl font-bold border-2 border-gray-300 rounded-lg focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-all"
          data-testid={`otp-input-${index}`}
        />
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-dark-900 via-dark-800 to-dark-900 flex items-center justify-center px-4" data-testid="admin-login-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="bg-white rounded-lg shadow-2xl p-8 w-full max-w-md"
      >
        {/* Header */}
        <div className="text-center mb-6">
          <h1 
            className="text-4xl font-bold mb-2 tracking-wider"
            style={{
              fontFamily: "'Racing Sans One', cursive",
              color: '#101957'
            }}
          >
            HADEN
          </h1>
          <p className="text-gray-600">
            {view === 'login' && 'Admin Login'}
            {view === 'otp-login' && 'Enter OTP Code'}
            {view === 'forgot-password' && 'Verify OTP'}
            {view === 'reset-password' && 'Set New Password'}
          </p>
        </div>

        {/* Error Message */}
        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start"
              data-testid="login-error"
            >
              <FiAlertCircle className="text-red-600 mt-0.5 mr-3 flex-shrink-0" />
              <p className="text-red-800 text-sm">{error}</p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Success Message */}
        <AnimatePresence>
          {success && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start"
            >
              <FiCheck className="text-green-600 mt-0.5 mr-3 flex-shrink-0" />
              <p className="text-green-800 text-sm">{success}</p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* LOGIN VIEW */}
        {view === 'login' && (
          <form onSubmit={handlePasswordLogin} className="space-y-5">
            {/* Email */}
            <div>
              <label className="block text-sm font-semibold text-dark-900 mb-2">Email</label>
              <div className="relative">
                <FiMail className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="admin@haden.in"
                  required
                  data-testid="login-email-input"
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-semibold text-dark-900 mb-2">Password</label>
              <div className="relative">
                <FiLock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="••••••••"
                  required
                  data-testid="login-password-input"
                />
              </div>
            </div>

            {/* Sign In Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3 bg-primary-600 text-white font-semibold rounded-lg hover:bg-primary-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
              data-testid="login-submit-btn"
            >
              {isLoading ? (
                <span className="flex items-center justify-center">
                  <div className="spinner mr-3"></div>
                  Signing in...
                </span>
              ) : (
                'Sign In'
              )}
            </button>

            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-gray-500">OR</span>
              </div>
            </div>

            {/* OTP Login Button */}
            <button
              type="button"
              onClick={handleRequestLoginOTP}
              disabled={isLoading}
              className="w-full py-3 border-2 border-primary-600 text-primary-600 font-semibold rounded-lg hover:bg-primary-50 transition-colors flex items-center justify-center disabled:opacity-50"
            >
              <FiSend className="mr-2" />
              Login with Email OTP
            </button>

            {/* Forgot Password Link */}
            <button
              type="button"
              onClick={handleRequestPasswordReset}
              disabled={isLoading}
              className="w-full py-2 text-gray-600 hover:text-primary-600 transition-colors flex items-center justify-center text-sm"
            >
              <FiKey className="mr-2" />
              Forgot Password?
            </button>
          </form>
        )}

        {/* OTP LOGIN VIEW */}
        {view === 'otp-login' && (
          <div className="space-y-4">
            <p className="text-center text-gray-600 text-sm">
              A 4-digit code was sent to<br />
              <strong className="text-dark-900">{email}</strong>
            </p>

            <OTPInput />

            <button
              onClick={handleVerifyLoginOTP}
              disabled={isLoading || otp.join('').length !== 4}
              className="w-full py-3 bg-primary-600 text-white font-semibold rounded-lg hover:bg-primary-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <span className="flex items-center justify-center">
                  <div className="spinner mr-3"></div>
                  Verifying...
                </span>
              ) : (
                'Verify & Login'
              )}
            </button>

            <div className="text-center">
              <button
                onClick={handleResendOTP}
                disabled={resendTimer > 0 || isLoading}
                className="text-sm text-gray-600 hover:text-primary-600 disabled:text-gray-400 disabled:cursor-not-allowed"
              >
                {resendTimer > 0 ? `Resend OTP (${resendTimer}s)` : "Didn't receive? Resend OTP"}
              </button>
            </div>

            <button
              onClick={backToLogin}
              className="w-full py-2 text-gray-600 hover:text-primary-600 transition-colors flex items-center justify-center text-sm"
            >
              <FiArrowLeft className="mr-2" />
              Back to Login
            </button>
          </div>
        )}

        {/* FORGOT PASSWORD VIEW (OTP Verification) */}
        {view === 'forgot-password' && (
          <div className="space-y-4">
            <p className="text-center text-gray-600 text-sm">
              Enter the OTP sent to<br />
              <strong className="text-dark-900">{email}</strong>
            </p>

            <OTPInput />

            <button
              onClick={handleVerifyResetOTP}
              disabled={isLoading || otp.join('').length !== 4}
              className="w-full py-3 bg-primary-600 text-white font-semibold rounded-lg hover:bg-primary-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <span className="flex items-center justify-center">
                  <div className="spinner mr-3"></div>
                  Verifying...
                </span>
              ) : (
                'Verify OTP'
              )}
            </button>

            <div className="text-center">
              <button
                onClick={handleResendOTP}
                disabled={resendTimer > 0 || isLoading}
                className="text-sm text-gray-600 hover:text-primary-600 disabled:text-gray-400 disabled:cursor-not-allowed"
              >
                {resendTimer > 0 ? `Resend OTP (${resendTimer}s)` : "Didn't receive? Resend OTP"}
              </button>
            </div>

            <button
              onClick={backToLogin}
              className="w-full py-2 text-gray-600 hover:text-primary-600 transition-colors flex items-center justify-center text-sm"
            >
              <FiArrowLeft className="mr-2" />
              Back to Login
            </button>
          </div>
        )}

        {/* RESET PASSWORD VIEW */}
        {view === 'reset-password' && (
          <form onSubmit={handleResetPassword} className="space-y-5">
            <div>
              <label className="block text-sm font-semibold text-dark-900 mb-2">New Password</label>
              <div className="relative">
                <FiLock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Min 8 characters"
                  required
                  minLength={8}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-dark-900 mb-2">Confirm Password</label>
              <div className="relative">
                <FiLock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className={`w-full pl-12 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    confirmPassword && confirmPassword !== newPassword
                      ? 'border-red-300 bg-red-50'
                      : confirmPassword && confirmPassword === newPassword
                      ? 'border-green-300 bg-green-50'
                      : 'border-gray-300'
                  }`}
                  placeholder="Confirm password"
                  required
                />
              </div>
              {confirmPassword && confirmPassword !== newPassword && (
                <p className="text-xs text-red-600 mt-1">Passwords do not match</p>
              )}
              {confirmPassword && confirmPassword === newPassword && (
                <p className="text-xs text-green-600 mt-1">Passwords match ✓</p>
              )}
            </div>

            <button
              type="submit"
              disabled={isLoading || !newPassword || newPassword !== confirmPassword}
              className="w-full py-3 bg-primary-600 text-white font-semibold rounded-lg hover:bg-primary-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <span className="flex items-center justify-center">
                  <div className="spinner mr-3"></div>
                  Resetting...
                </span>
              ) : (
                'Reset Password'
              )}
            </button>

            <button
              type="button"
              onClick={backToLogin}
              className="w-full py-2 text-gray-600 hover:text-primary-600 transition-colors flex items-center justify-center text-sm"
            >
              <FiArrowLeft className="mr-2" />
              Back to Login
            </button>
          </form>
        )}

        {/* Back to Site */}
        <div className="mt-6 text-center">
          <a href="/" className="text-sm text-primary-600 hover:text-primary-700">
            ← Back to website
          </a>
        </div>
      </motion.div>
    </div>
  );
}

export default AdminLogin;
